import { Injectable, inject, signal, computed } from '@angular/core';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import { of } from 'rxjs';

export interface IBreadcrumbItem {
    label: string;
    url?: string;
}

@Injectable({
    providedIn: 'root',
})
export class BreadcrumbService {
    private readonly router = inject(Router);
    private readonly activatedRoute = inject(ActivatedRoute);

    // Signal para armazenar os itens do breadcrumb
    private readonly breadcrumbItems = signal<IBreadcrumbItem[]>([]);

    // Computed signal para os itens do breadcrumb
    readonly items = computed(() => this.breadcrumbItems());

    constructor() {
        this.initializeBreadcrumbTracking();
    }

    /**
     * Inicializa o rastreamento de breadcrumb baseado na navegação
     */
    private initializeBreadcrumbTracking(): void {
        this.router.events
            .pipe(
                filter(event => event instanceof NavigationEnd || (event as any)?.routerEvent instanceof NavigationEnd),
                map(() => this.activatedRoute),
                switchMap(route => this.getCurrentRouteData(route)),
                map(routeData => this.buildBreadcrumbFromRouteData(routeData))
            )
            .subscribe(items => {
                this.breadcrumbItems.set(items);
            });
    }

    /**
     * Obtém os dados da rota atual
     */
    private getCurrentRouteData(route: ActivatedRoute) {
        // Navega até a rota mais profunda (child)
        let currentRoute = route;
        while (currentRoute.firstChild) {
            currentRoute = currentRoute.firstChild;
        }

        // Retorna os dados da rota atual
        return currentRoute.data;
    }

    /**
     * Constrói breadcrumb baseado nos dados da rota
     */
    private buildBreadcrumbFromRouteData(routeData: any): IBreadcrumbItem[] {
        const items: IBreadcrumbItem[] = [];

        // Adiciona item "Home" sempre como primeiro
        items.push({
            label: 'Home',
            url: '/',
        });
        // Adiciona título da rota atual se existir
        if (routeData?.title) {
            items.push({
                label: routeData.title,
                url: routeData.url || undefined,
            });
        }

        // Adiciona intermediários baseado na URL atual
        const currentUrl = this.router.url;

        const addIntermediateItem = (intermediateItem: IBreadcrumbItem) => {
            if (items.length > 1) {
                const currentItem = items.pop();
                items.push(intermediateItem);
                if (currentItem) {
                    items.push(currentItem);
                }
            }
        };

        if (currentUrl.includes('/check-veiculo/')) {
            const intermediateItem = {
                label: 'Check Viatura',
                url: '/check-veiculo',
            };
            addIntermediateItem(intermediateItem);
        }

        if (currentUrl.includes('/despacho-ocorrencia/')) {
            const intermediateItem = {
                label: 'Despacho de Ocorrência',
            };
            addIntermediateItem(intermediateItem);
        }

        if (currentUrl.includes('/relatorio-ocorrencia/')) {
            const intermediateItem = {
                label: 'Relatório de Ocorrência',
            };
            addIntermediateItem(intermediateItem);
        }

        if (currentUrl.includes('/relatorio-servico-motorizado/')) {
            const intermediateItem = {
                label: 'Serviço Motorizado',
            };
            addIntermediateItem(intermediateItem);
        }

        if (currentUrl.includes('/cadastro-abastecimento/')) {
            const intermediateItem = {
                label: 'Abastecimento',
            };
            addIntermediateItem(intermediateItem);
        }

        if (currentUrl.includes('/checklist-veiculo/')) {
            const intermediateItem = {
                label: 'Checklist Viatura',
            };
            addIntermediateItem(intermediateItem);
        }

        if (currentUrl.includes('/checklist-porteiro/')) {
            const intermediateItem = {
                label: 'Checklist Porteiro',
            };
            addIntermediateItem(intermediateItem);
        }

        return items;
    }
}
